import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { regionMapping, type RegionOption } from "../../../utils/regionMapping";
import DeletableChip from "./DeletableChip";

interface VirtualizedRegionDropdownProps {
  value: string[];
  onChange: (regions: string[]) => void;
  label?: string;
  placeholder?: string;
}

// Custom option component for better performance
const RegionOptionComponent = React.memo(
  ({ option, props }: { option: RegionOption; props: any }) => {
    // Get the region item for display
    const regionItem = regionMapping.getById(option.value);
    if (!regionItem) return null;

    return (
      <Box
        component="li"
        {...props}
        sx={{ fontSize: "0.875rem", padding: "8px 16px" }}
      >
        <Box>
          <Typography
            variant="body2"
            component="span"
            sx={{ fontWeight: 500, color: "#1976d2" }}
          >
            {/* {regionItem.type === 'region' ? '🌍 ' : ''}{regionItem.common} */}
            {regionItem.common}
          </Typography>
          {/* {regionItem.type === 'country' && (
          <Typography variant="caption" component="span" sx={{ ml: 1, color: '#888', display: 'block' }}>
            Country
          </Typography>
        )} */}
        </Box>
      </Box>
    );
  }
);

const VirtualizedRegionDropdown: React.FC<VirtualizedRegionDropdownProps> = ({
  value: selectedRegions,
  onChange: onRegionsChange,
  label,
  placeholder = "Search regions and countries...",
}) => {
  const [inputValue, setInputValue] = useState("");

  // Get all options once and reuse the same references
  const allOptions = useMemo(() => regionMapping.getAllOptions(), []);

  const selectedOptions = useMemo(() => {
    return allOptions.filter((option) =>
      selectedRegions.includes(option.value)
    );
  }, [selectedRegions, allOptions]);

  // Optimized filtering with smart ranking for better UX
  // Always include selected options in the filtered list
  const filteredOptions = useMemo(() => {
    let filtered: RegionOption[];
    if (!inputValue.trim()) {
      filtered = allOptions.slice(0, 100); // Show first 100 by default
    } else {
      filtered = regionMapping.searchByName(inputValue);
    }

    // Ensure all selected options are included in the filtered list
    const filteredIds = new Set(filtered.map((opt) => opt.value));
    const missingSelected = selectedOptions.filter(
      (opt) => !filteredIds.has(opt.value)
    );

    return [...missingSelected, ...filtered];
  }, [inputValue, allOptions, selectedOptions]);

  const handleChange = useCallback(
    (event: any, newValue: RegionOption[]) => {
      const newIds = newValue.map((option) => option.value);
      onRegionsChange(newIds);
    },
    [onRegionsChange]
  );

  const handleInputChange = useCallback((event: any, newInputValue: string) => {
    setInputValue(newInputValue);
  }, []);

  return (
    <Autocomplete
      multiple
      id="regions-autocomplete"
      options={filteredOptions}
      value={selectedOptions}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      getOptionLabel={(option: RegionOption) => option.label}
      isOptionEqualToValue={(option: RegionOption, value: RegionOption) =>
        option.value === value.value
      }
      filterOptions={(options) => options} // We handle filtering manually for performance
      limitTags={3}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || "Regions & Countries"}
          placeholder={selectedOptions.length === 0 ? placeholder : ""}
          variant="outlined"
          size="small"
          InputLabelProps={{
            ...params.InputLabelProps,
            sx: {
              color: "#e0e0e0",
              "&.MuiInputLabel-shrink": {
                transform: "translate(14px, -9px) scale(0.75)",
              },
            },
            shrink: selectedOptions.length > 0 || inputValue.length > 0,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#1e1e1e",
              "& fieldset": {
                borderColor: "#555",
              },
              "&:hover fieldset": {
                borderColor: "#777",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#1976d2",
              },
            },
            "& .MuiInputBase-input": {
              color: "#e0e0e0",
            },
            "& .MuiInputLabel-root": {
              color: "#e0e0e0",
              "&.Mui-focused": {
                color: "#1976d2",
              },
            },
          }}
        />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option: RegionOption, index) => {
          const { key, onDelete, ...tagProps } = getTagProps({ index });
          return (
            <DeletableChip
              key={option.value}
              label={option.label}
              onDelete={() => {
                const newRegions = selectedRegions.filter(
                  (id: string) => id !== option.value
                );
                onRegionsChange(newRegions);
              }}
              maxChars={20}
              {...tagProps}
            />
          );
        })
      }
      renderOption={(props, option) => {
        const { key, ...otherProps } = props;
        return (
          <RegionOptionComponent
            key={key || option.value}
            option={option}
            props={otherProps}
          />
        );
      }}
      PaperComponent={({ children, ...props }) => (
        <Box
          {...props}
          sx={{
            backgroundColor: "background.raised",
            color: "#ffffff",
            "& .MuiAutocomplete-option": {
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
              '&[aria-selected="true"]': {
                backgroundColor: "rgba(25, 118, 210, 0.2)",
              },
            },
          }}
        >
          {children}
        </Box>
      )}
    />
  );
};

export default VirtualizedRegionDropdown;
